/**
 * SOLID Review
 * S — Strong. Single job: validate and hold a normalized (lowercased/trimmed) email string. All logic
 *     (format regex, length ≤320, non-empty) serves that one purpose.
 * O — Good. New validation rules are added inside create()'s guard chain without touching callers; the
 *     public surface (create/parse/isValid/normalised/value) is stable.
 * L — N/A — no inheritance hierarchy (private constructor blocks subclassing entirely).
 * I — Strong. Four factories and checks plus a value getter; the session asks for what it uses.
 * D — Strong. Zero imports; depends on nothing but the language runtime.
 */

/**
 * AN ADDRESS THE PLAYER TYPED. The account's rules ask it (`session/ClientSession.ts`, frontend-domain-oo P4):
 * whether a typed address is one, and the address as the server is sent it — trimmed, lowercased.
 * `equals` and `toString` went in P4: nothing but their test read them (§28.267's finding).
 */
export class EmailAddress {
  private readonly _value: string;

  private constructor(value: string) {
    this._value = value;
  }

  static create(raw: string): EmailAddress {
    if (!raw || typeof raw !== 'string') {
      throw new Error('EmailAddress: value must be a non-empty string');
    }
    const trimmed = EmailAddress.normalised(raw);
    if (trimmed.length === 0) {
      throw new Error('EmailAddress: value must not be empty');
    }
    if (trimmed.length > 320) {
      throw new Error('EmailAddress: exceeds maximum length of 320 characters');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      throw new Error(`EmailAddress: invalid email format`);
    }
    return new EmailAddress(trimmed);
  }

  /** The address `raw` is, or none when it is not one. */
  static parse(raw: string): EmailAddress | null {
    try {
      return EmailAddress.create(raw);
    } catch {
      return null;
    }
  }

  static isValid(raw: string): boolean {
    return EmailAddress.parse(raw) !== null;
  }

  /** An address as the server is sent it, checked or not: trimmed and lowercased. */
  static normalised(raw: string): string {
    return raw.trim().toLowerCase();
  }

  get value(): string {
    return this._value;
  }
}
